/**
 * AutoRefresh — global auto-refresh provider + hook.
 *
 * Goals:
 *  - Every 30s background refetch of subscribed page-level data
 *  - Manual invalidation by scope (e.g. after a status change, an action)
 *  - "Refreshing" indicator state available to UI
 *  - Pause when document is hidden (battery / quota friendly)
 *  - Survives form state (only data is refetched, no remount)
 *
 * Usage in a page:
 *   const { isRefreshing } = useAutoRefresh(fetchData, { scope: 'leaves', interval: 30000 });
 *
 *   // Anywhere in the app:
 *   const { triggerRefresh } = useRefresh();
 *   triggerRefresh();             // refresh ALL subscribers
 *   triggerRefresh('leaves');     // refresh only 'leaves' subscribers + cross-scope ones flagged 'all'
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';

const RefreshContext = createContext(null);

export const RefreshProvider = ({ children, defaultInterval = 30000 }) => {
  // Bumping `globalCounter` causes ALL subscribers to refetch.
  const [globalCounter, setGlobalCounter] = useState(0);
  // Per-scope counters allow targeted invalidation.
  const [scopeCounters, setScopeCounters] = useState({});
  // Count of currently-active refreshers (so global spinner can show).
  const activeCountRef = useRef(0);
  const [activeCount, setActiveCount] = useState(0);
  const pausedRef = useRef(false);

  const triggerRefresh = useCallback((scope) => {
    if (!scope) {
      setGlobalCounter((c) => c + 1);
    } else {
      setScopeCounters((prev) => ({ ...prev, [scope]: (prev[scope] || 0) + 1 }));
    }
  }, []);

  const beginActive = useCallback(() => {
    activeCountRef.current += 1;
    setActiveCount(activeCountRef.current);
  }, []);
  const endActive = useCallback(() => {
    activeCountRef.current = Math.max(0, activeCountRef.current - 1);
    setActiveCount(activeCountRef.current);
  }, []);

  // Pause auto-refresh while tab is hidden
  useEffect(() => {
    const onVis = () => { pausedRef.current = document.hidden; };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Listen to a custom DOM event for cross-component triggers (e.g. NotificationBell)
  useEffect(() => {
    const handler = (e) => {
      const scope = e?.detail?.scope || null;
      triggerRefresh(scope);
    };
    window.addEventListener('crm:refresh', handler);
    return () => window.removeEventListener('crm:refresh', handler);
  }, [triggerRefresh]);

  return (
    <RefreshContext.Provider
      value={{
        globalCounter,
        scopeCounters,
        defaultInterval,
        triggerRefresh,
        beginActive,
        endActive,
        activeCount,
        isPausedRef: pausedRef,
      }}
    >
      {children}
    </RefreshContext.Provider>
  );
};

export const useRefresh = () => {
  const ctx = useContext(RefreshContext);
  if (!ctx) {
    // Safe fallback so pages that mount outside provider don't crash
    return { triggerRefresh: () => {}, activeCount: 0 };
  }
  return ctx;
};

/**
 * useAutoRefresh — subscribe a page-level fetch function to global auto-refresh.
 *
 * @param fetchFn  - Async () => void that re-fetches the page's data.
 * @param options  - { interval?: ms, scope?: string, enabled?: boolean, runOnMount?: boolean }
 *
 * Behavior:
 *  - Runs fetchFn on mount (if runOnMount, default true)
 *  - Runs every `interval` ms while tab visible (defaults to provider's defaultInterval)
 *  - Runs whenever globalCounter or scope counter increments
 *  - Exposes { isRefreshing } so pages can show a subtle spinner
 */
export const useAutoRefresh = (fetchFn, options = {}) => {
  const { interval, scope, enabled = true, runOnMount = true } = options;
  const ctx = useContext(RefreshContext);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const fnRef = useRef(fetchFn);
  fnRef.current = fetchFn;

  const effInterval = interval ?? ctx?.defaultInterval ?? 30000;
  const scopeCounter = scope && ctx?.scopeCounters ? (ctx.scopeCounters[scope] || 0) : 0;
  const globalCounter = ctx?.globalCounter || 0;

  const doRefresh = useCallback(async () => {
    if (!fnRef.current) return;
    setIsRefreshing(true);
    ctx?.beginActive?.();
    try {
      await fnRef.current();
    } catch (_) {
      // swallow — page itself should handle errors in fetchFn
    } finally {
      setIsRefreshing(false);
      ctx?.endActive?.();
    }
  }, [ctx]);

  // Initial mount + react to invalidation counters
  useEffect(() => {
    if (!enabled) return;
    if (!runOnMount && globalCounter === 0 && scopeCounter === 0) return;
    doRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, globalCounter, scopeCounter]);

  // Polling loop
  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => {
      if (ctx?.isPausedRef?.current) return;
      doRefresh();
    }, effInterval);
    return () => clearInterval(id);
  }, [enabled, effInterval, doRefresh, ctx]);

  return { isRefreshing, refresh: doRefresh };
};

/**
 * Cross-component refresh trigger — can be called from anywhere (no hook ctx needed).
 * Dispatches a CustomEvent that RefreshProvider listens for.
 */
export const triggerGlobalRefresh = (scope) => {
  try {
    window.dispatchEvent(new CustomEvent('crm:refresh', { detail: { scope: scope || null } }));
  } catch (_) { /* SSR safety */ }
};
